// Ping /api/pv : le backend forge ensuite un hit Matomo avec l'IP/UA réels
// du visiteur. Invisible des adblockers (requête /api/ vers le domaine
// principal, pas de tracker JS chargé côté navigateur).

import axios from 'axios';
import { resetScrollForPage } from './scrollTracker';

const getApiUrl = (): string => {
  const env = process.env.REACT_APP_API_URL;
  if (env) return env;
  const host = window.location.hostname;
  return `http://${host}:4010/api`;
};

// Dédup contre React.StrictMode (qui tire chaque useEffect 2x en dev) et
// les doubles hits réseau. On garde le dernier path envoyé + son timestamp ;
// fenêtre courte (200 ms) pour ne pas bloquer un retour légitime sur la
// même vue plus tard dans la session.
let lastSent: { path: string; at: number } = { path: '', at: 0 };
const DEDUP_WINDOW_MS = 200;

// Visitor ID stable persistant (UUID 16 hex) pour permettre à Matomo de
// reconnaître un même utilisateur cross-day. Si localStorage indisponible
// (navigation privée stricte, quota), le serveur retombe sur son hash
// IP+UA+jour : aucun hit n'est perdu, juste un visiteur unique recréé.
const VISITOR_ID_KEY = 'pv_visitor_id';

// Cleanup retroactif : supprime un eventuel cookie plausible_ignore=1 pose
// avant la migration mai 2026 (Plausible retire). Suppression douce, ne casse
// rien si le cookie n'existe pas.
if (typeof document !== 'undefined' && document.cookie.includes('plausible_ignore=')) {
  document.cookie = 'plausible_ignore=; path=/; max-age=0; SameSite=Lax';
}

function getOrCreateVisitorId(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    let id = localStorage.getItem(VISITOR_ID_KEY);
    if (!id || !/^[a-f0-9]{16}$/.test(id)) {
      if (typeof crypto === 'undefined' || !crypto.getRandomValues) return null;
      const bytes = new Uint8Array(8);
      crypto.getRandomValues(bytes);
      id = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      localStorage.setItem(VISITOR_ID_KEY, id);
    }
    // Miroir cookie 1 an : permet aux routes server-side (POST /api/membres,
    // /api/relations, etc.) de relier les events backend a la meme session que
    // les pageviews via readVisitorIdCookie(). Sans ce cookie, le serveur retombe
    // sur son hash IP+UA+jour et l'event apparait comme une visite distincte.
    if (typeof document !== 'undefined') {
      document.cookie = `${VISITOR_ID_KEY}=${id}; path=/; max-age=31536000; SameSite=Lax`;
    }
    return id;
  } catch {
    return null;
  }
}

export function trackPageview(virtualPath?: string): void {
  if (typeof document !== 'undefined') {
    if (document.cookie.includes('matomo_ignore=1')) return;
  }

  // Path "virtuel" = /, /dashboard, /timeline, /tree pour distinguer les vues
  // du SPA dans Matomo (le navigateur ne change jamais d'URL ici).
  const path = virtualPath && virtualPath.startsWith('/') ? virtualPath : window.location.pathname;

  const now = Date.now();
  if (lastSent.path === path && now - lastSent.at < DEDUP_WINDOW_MS) return;
  lastSent = { path, at: now };

  const referrer = typeof document !== 'undefined' ? document.referrer : '';
  const title = typeof document !== 'undefined' ? document.title : '';
  const vid = getOrCreateVisitorId();

  const body: Record<string, string> = { path, referrer, title };
  if (vid) body.vid = vid;
  // Resolution ecran (rapport "Resolution" Matomo, sinon vide en pur server-side).
  if (typeof window !== 'undefined' && window.screen?.width && window.screen?.height) {
    body.res = `${window.screen.width}x${window.screen.height}`;
  }

  axios
    .post(`${getApiUrl()}/pv`, body)
    .catch(() => {});

  // HEARTBEAT 15s : prolonge la visite Matomo. Reset des secondes a chaque
  // nouvelle pageview (= nouvel appel de trackPageview).
  startHeartbeat(path);
  // Reset des seuils scroll (25/50/75/100%) pour la nouvelle vue.
  resetScrollForPage(path);
}

// === HEARTBEAT (depuis 2026-05) ===
// Timer 15s tant que l'onglet est visible. POST /api/pv-ping → ping=1 Matomo
// qui prolonge la visite sans creer d'action. Cap 30 pings (~7,5 min) par
// pageview pour eviter de tracker les onglets oublies.
const HEARTBEAT_INTERVAL_MS = 15_000;
const HEARTBEAT_MAX_PINGS = 30;

let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let heartbeatPings = 0;
let heartbeatPath = '';

function trackingDisabled(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.includes('matomo_ignore=1');
}

function sendPing(): void {
  if (heartbeatPings >= HEARTBEAT_MAX_PINGS) {
    stopHeartbeat();
    return;
  }
  if (trackingDisabled()) return;
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

  heartbeatPings++;

  const vid = getOrCreateVisitorId();
  const body: Record<string, string> = { path: heartbeatPath };
  if (vid) body.vid = vid;

  axios.post(`${getApiUrl()}/pv-ping`, body).catch(() => {});
}

function startHeartbeat(path: string): void {
  stopHeartbeat();
  heartbeatPings = 0;
  heartbeatPath = path;
  heartbeatTimer = setInterval(sendPing, HEARTBEAT_INTERVAL_MS);
}

export function stopHeartbeat(): void {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}
